"use client";

import {
  IconArrowsMaximize,
  IconChartPie4Filled,
  IconChartPie,
} from "@tabler/icons-react";
import { usePrenotaStore, useRateio, RateioRadialChart } from "@PN/inclusao";
import { Badge, cn, ChartConfig, fmt } from "visuais";
import React, { useMemo } from "react";
import { motion } from "motion/react";

interface RateioCardProps {
  className?: string;
  onExpand: () => void;
}

export function RateioCard({ className, onExpand }: RateioCardProps) {
  const draft = usePrenotaStore((state) => state.draft);
  const aux = usePrenotaStore((state) => state.aux);
  const { calculations } = useRateio();

  // ✅ CORRIGIDO: Converte corretamente valor brasileiro (com vírgula) para número
  const hasValorParaRatear = useMemo(() => {
    if (!aux.totalNota || typeof aux.totalNota !== "object") return false;
    const totalStr = String((aux.totalNota as any).total || "0");
    // Converte formato brasileiro (vírgula) para formato numérico (ponto)
    const totalNum = Number(totalStr.replace(",", ".")) || 0;
    return totalNum > 0;
  }, [aux.totalNota]);
  const hasRateioConfigurado = calculations.totalRateado > 0;

  // Dados para gráfico de filiais
  const filiaisChartData = useMemo(() => {
    if (calculations.totalGeral === 0) return [];

    const filiaisData: Record<string, number> = {};
    draft.rateios.forEach((rateio) => {
      filiaisData[rateio.filial] =
        (filiaisData[rateio.filial] || 0) + rateio.valor;
    });

    return [filiaisData];
  }, [draft.rateios, calculations.totalGeral]);

  // Dados para gráfico de centros de custo
  const centrosChartData = useMemo(() => {
    if (calculations.totalGeral === 0) return [];

    const centrosData: Record<string, number> = {};
    draft.rateios.forEach((rateio) => {
      centrosData[rateio.centroCusto] =
        (centrosData[rateio.centroCusto] || 0) + rateio.valor;
    });

    return [centrosData];
  }, [draft.rateios, calculations.totalGeral]);

  // Configuração para filiais (cores dinâmicas)
  const filiaisConfig = useMemo(() => {
    const config: ChartConfig = {};
    const colors = ["#99f6e4", "#2dd4bf", "#0d9488", "#115e59", "#042f2e"];

    Object.keys(filiaisChartData[0] || {}).forEach((filial, index) => {
      config[filial] = {
        label: filial,
        color: colors[index % colors.length],
      };
    });

    return config;
  }, [filiaisChartData]);

  // Configuração para centros de custo (cores dinâmicas)
  const centrosConfig = useMemo(() => {
    const config: ChartConfig = {};
    const colors = ["#99f6e4", "#2dd4bf", "#0d9488", "#115e59", "#042f2e"];

    Object.keys(centrosChartData[0] || {}).forEach((centro, index) => {
      config[centro] = {
        label: centro,
        color: colors[index % colors.length],
      };
    });

    return config;
  }, [centrosChartData]);

  return (
    <motion.div
      layoutId="rateio-card"
      className={cn(
        "w-full h-full rounded-lg flex flex-col p-3 border bg-gradient-to-br cursor-pointer transition-all hover:scale-[1.02] from-cyan-50 to-cyan-100 dark:from-cyan-950/30 dark:to-cyan-900/20 border-cyan-200 dark:border-cyan-800",
        !hasValorParaRatear && "cursor-not-allowed opacity-50",
        className
      )}
      onClick={() => hasValorParaRatear && onExpand()}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      title={
        !hasValorParaRatear
          ? "Adicione produtos primeiro para configurar o rateio"
          : "Clique para configurar o rateio"
      }
    >
      {/* Header integrado */}
      <div className="flex items-center justify-between w-full pb-2">
        <div className="flex items-center gap-2">
          <IconChartPie4Filled className="size-4 text-cyan-600 dark:text-cyan-400" />
          <span className="text-xs font-medium">Rateio</span>
          <div
            className={`w-2 h-2 rounded-full ${
              hasValorParaRatear ? "bg-green-500" : "bg-amber-400"
            }`}
          />
        </div>
        <div className="flex items-center gap-2">
          {calculations.totalGeral > 0 && calculations.restante > 0 && (
            <Badge
              variant="outline"
              className="h-5 px-1.5 text-[7pt] text-orange-600 dark:text-orange-400 border-orange-300 dark:border-orange-600"
            >
              Restante: {fmt.numbers.currency(calculations.restante)}
            </Badge>
          )}
          <IconArrowsMaximize className="size-4 text-cyan-600 dark:text-cyan-400" />
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="flex-1 flex flex-col items-center justify-center space-y-1 h-full">
        {!hasValorParaRatear || !hasRateioConfigurado ? (
          // Empty State centralizado
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              className="mb-4"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                delay: 0.3,
                duration: 0.8,
                type: "spring",
                stiffness: 120,
                damping: 15,
              }}
            >
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-cyan-100 via-teal-100 to-emerald-100 dark:from-cyan-900/20 dark:via-teal-900/20 dark:to-emerald-900/20 rounded-full flex items-center justify-center shadow-xl dark:shadow-cyan-900/30">
                <IconChartPie className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
              </div>
            </motion.div>
            <h3 className="text-base font-semibold text-foreground mb-2">
              {!hasValorParaRatear
                ? "Adicione produtos primeiro"
                : "Configure o rateio"}
            </h3>
          </motion.div>
        ) : (
          // Gráficos de rateio
          <div className="w-full h-full pb-5">
            <div className="grid grid-cols-2 gap-2 h-full">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="flex-1 flex items-center justify-center">
                  <RateioRadialChart
                    data={filiaisChartData}
                    config={filiaisConfig}
                    total={calculations.totalGeral}
                    label="Filiais"
                    className="h-24"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex-1 flex items-center justify-center">
                  <RateioRadialChart
                    data={centrosChartData}
                    config={centrosConfig}
                    total={calculations.totalGeral}
                    label="Centro Custo"
                    className="h-24"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
